import { X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useCloseOnBack } from '@/hooks/useCloseOnBack'
import { cn } from '@/lib/utils'
import type { DocumentType } from '@/models/document'
import { QuoteCard } from './QuoteCard'
import { QuoteCardFocusReveal } from './QuoteCardFocusReveal'
import { QuoteCardMini } from './QuoteCardMini'
import { QuoteCardSkeleton } from './QuoteCardSkeleton'
import { type PricedQuoteCard, QUOTE_CARD_WIDTH, type QuoteCardData, QuoteCardStage } from './quoteCardTypes'
import { cardCartKeys } from './quoteCartItems'
import {
  CardOpenSource,
  quoteCardProperties,
  QuotesEvent,
  startTiming,
  useTrackQuotesEvent,
} from './quotesAnalytics'

// Comparison is capped at two quotes side by side, never three or more.
const MAX_COMPARISON = 2

// One card per click: card width plus the gap between them. 432 is the full 26rem card.
const FALLBACK_CARD_STEP = 432
const CARD_GAP = 16

// Measured, since the card's width follows the viewport on phones. A marked wrapper, not
// the first child: with no quotes yet, that's the sr-only status line.
function cardStep(scroller: HTMLElement | null): number {
  const card = scroller?.querySelector(':scope > [data-carousel-card]')
  return card ? card.getBoundingClientRect().width + CARD_GAP : FALLBACK_CARD_STEP
}
// Small buffer so floating-point scroll positions don't leave the fade stuck on.
const EDGE_THRESHOLD = 4
// One analytics event per gesture rather than per scroll frame.
const SCROLL_REPORT_DELAY = 400

type ScrollSource = 'arrow' | 'drag' | 'filter_reset' | 'new_quote' | 'package'

/** How far into the scroller the first quote starts: the leading card's slot, or 0 without one. */
function quotesOffset(scroller: HTMLElement): number {
  const firstQuote = scroller.querySelector<HTMLElement>('[data-quote-id]')
  const firstSlot = scroller.firstElementChild
  if (!firstQuote || !(firstSlot instanceof HTMLElement)) return 0
  return firstQuote.offsetLeft - firstSlot.offsetLeft
}

interface QuoteCardsCarouselProps {
  quotes: QuoteCardData[]
  /** Changes whenever the sort or the active filters change — scrolls the carousel back to the first card. */
  orderKey: string
  /** Add-to-cart handler wired to the focused card's coverage CTAs. */
  onAddCoverages?: (coverageKeys: string[]) => void
  /** Remove-from-cart handler wired to the focused card's coverage CTAs. */
  onRemoveCoverages?: (coverageKeys: string[]) => void
  /** Coverage keys currently in the cart — drives the focused card's added/remove states. */
  cartKeys?: ReadonlySet<string>
  /** Hides every add-to-cart affordance. Set where no checkout exists to receive the cart. */
  hideCheckout?: boolean
  /** Removes a quote the customer uploaded themselves. Their cards show no delete without it. */
  onDeleteQuote?: (id: string) => void
  /** Rendered as a first slot before the quotes, e.g. the customer's package. Sort and filters leave it first. */
  leadingCard?: ReactNode
  /** Rendered as a final slot after the last quote, e.g. the upload-your-own-quote card. */
  trailingCard?: ReactNode
  /** Carriers asked for a price whose cards don't exist yet — one skeleton each, after the quotes. */
  awaitingCarrierCount?: number
  /** Marks that count a page-load guess rather than a real tally, so it isn't announced as one. */
  isLoading?: boolean
  /** Fires whenever either end of the scroller is reached, so external arrows can disable. */
  onEdgeChange?: (edges: { canScrollPrev: boolean; canScrollNext: boolean }) => void
  /** Opens the focused card in three beats instead of at full size. */
  revealFocusedCard?: boolean
  /** Opens a carrier's terms from its card. Without it those cards show no Review action. */
  onReviewAttestation?: (id: string) => void
  /** Opens the loss-run upload from its card. Without it those cards show no Upload action. */
  onUploadDocument?: (id: string, documentType: DocumentType) => void
}

/** Wraps the focused card in the reveal only when asked; a bare card opens at full size. */
function FocusedCardShell({ reveal, children }: { reveal?: boolean; children: ReactNode }) {
  if (!reveal) return children
  return <QuoteCardFocusReveal>{children}</QuoteCardFocusReveal>
}

/** Imperative scroll controls, so external controls (e.g. the mobile header) can drive the carousel. */
export interface QuoteCarouselHandle {
  scrollPrev: () => void
  scrollNext: () => void
  /** Brings one card to the front of the scroller — e.g. the quote the customer just uploaded. */
  scrollToQuote: (id: string) => void
  /** Back to the first slot, where the package sits. */
  scrollToStart: () => void
}

/** Horizontal, left-to-right scrolling carousel of QuoteCards for the Quotes page's main list. */
export const QuoteCardsCarousel = forwardRef<QuoteCarouselHandle, QuoteCardsCarouselProps>(
  function QuoteCardsCarousel(
    {
      quotes,
      orderKey,
      onAddCoverages,
      onRemoveCoverages,
      cartKeys,
      hideCheckout,
      onDeleteQuote,
      leadingCard,
      trailingCard,
      awaitingCarrierCount = 0,
      isLoading,
      onEdgeChange,
      revealFocusedCard,
      onReviewAttestation,
      onUploadDocument,
    },
    ref
  ) {
    const track = useTrackQuotesEvent()
    const scrollerRef = useRef<HTMLDivElement>(null)
    const isFirstRender = useRef(true)
    // What is currently open, with each card's own stopwatch and the identity it was opened
    // with — a card deleted or re-fetched away can no longer describe itself at close time.
    const openCardsRef = useRef(
      new Map<string, { elapsed: () => number; properties: Record<string, unknown> }>()
    )
    const scrollReportRef = useRef<number>(undefined)
    // An arrow scrolls the same scroller a drag does, so the source is stamped before the scroll.
    const scrollSourceRef = useRef<ScrollSource>('drag')
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    // Ids shown in the focused viewer (a blur-scrim modal). One = single card; two = side-by-side
    // comparison. comparisonIds[0] is the anchor quote; the second slot is the one being compared.
    const [comparisonIds, setComparisonIds] = useState<string[]>([])

    function updateEdgeFades() {
      const el = scrollerRef.current
      if (!el) return
      const canScrollPrev = el.scrollLeft > EDGE_THRESHOLD
      const canScrollNext = el.scrollLeft + el.clientWidth < el.scrollWidth - EDGE_THRESHOLD
      setCanScrollLeft(canScrollPrev)
      setCanScrollRight(canScrollNext)
      onEdgeChange?.({ canScrollPrev, canScrollNext })
    }

    function reportScroll() {
      const el = scrollerRef.current
      if (!el) return
      track(QuotesEvent.CAROUSEL_SCROLLED, {
        source: scrollSourceRef.current,
        // Counted from the first quote, so a leading card doesn't shift every index by one.
        card_index: Math.round(Math.max(0, el.scrollLeft - quotesOffset(el)) / cardStep(el)),
        quote_count: quotes.length,
        reached_end: el.scrollLeft + el.clientWidth >= el.scrollWidth - EDGE_THRESHOLD,
      })
      scrollSourceRef.current = 'drag'
    }

    /** Scrolls to `left`, stamping where the scroll came from first. */
    function scrollToLeft(left: number, source: ScrollSource) {
      const scroller = scrollerRef.current
      // Already there fires no scroll event, and the stamp would then mislabel the next drag.
      if (!scroller || Math.abs(scroller.scrollLeft - left) <= EDGE_THRESHOLD) return
      scrollSourceRef.current = source
      scroller.scrollTo({ left, behavior: 'smooth' })
    }

    function onScroll() {
      updateEdgeFades()
      window.clearTimeout(scrollReportRef.current)
      scrollReportRef.current = window.setTimeout(reportScroll, SCROLL_REPORT_DELAY)
    }

    useEffect(() => () => window.clearTimeout(scrollReportRef.current), [])

    // The package slot changes the scroll width on its own, so its arrival re-checks the arrows too.
    const hasLeadingCard = !!leadingCard
    useEffect(() => {
      updateEdgeFades()
    }, [quotes, awaitingCarrierCount, hasLeadingCard])

    // Priced quotes are the only ones that can be focused/compared; they populate the rail.
    const pricedQuotes = quotes.filter(
      (quote): quote is PricedQuoteCard => quote.stage === QuoteCardStage.PRICED
    )
    // Resolve to actual quotes, dropping any id that's been filtered out of the list.
    const comparedQuotes = comparisonIds
      .map((id) => pricedQuotes.find((quote) => quote.id === id))
      .filter((quote): quote is PricedQuoteCard => Boolean(quote))
    const isOpen = comparedQuotes.length > 0

    // Only offer quotes you can meaningfully compare against the anchor (first selected) — i.e. that
    // share at least one of the anchor's coverages. The anchor itself is dropped (it's already the
    // main card); a second selected quote stays listed (active) so it can be toggled off from the rail.
    const anchorQuote = comparedQuotes[0]
    const railQuotes =
      comparedQuotes.length === 0
        ? pricedQuotes
        : pricedQuotes.filter(
            (quote) =>
              quote.id !== anchorQuote.id &&
              (comparisonIds.includes(quote.id) ||
                anchorQuote.coverages.some((anchorCoverage) =>
                  quote.coverages.some((coverage) => coverage.type === anchorCoverage.type)
                ))
          )
    // No comparable quotes → don't show the compare rail at all (and narrow back to a single card).
    const showRail = railQuotes.length > 0
    // Wide enough to keep a six-figure premium's estimate tag on the price line.
    const contentWidth =
      comparedQuotes.length >= 2 ? 'md:max-w-6xl' : showRail ? 'md:max-w-3xl' : 'md:max-w-md'

    function cardProperties(id: string) {
      const index = quotes.findIndex((quote) => quote.id === id)
      return index === -1 ? { indication_id: id } : quoteCardProperties(quotes[index], index)
    }

    function openCard(id: string, source: CardOpenSource) {
      const properties = cardProperties(id)
      openCardsRef.current.set(id, { elapsed: startTiming(), properties })
      track(QuotesEvent.CARD_OPENED, { ...properties, source })
    }

    /** One close per open, carrying that card's own dwell time — what says whether it was read. */
    function closeCard(id: string) {
      const open = openCardsRef.current.get(id)
      if (!open) return
      openCardsRef.current.delete(id)
      track(QuotesEvent.CARD_CLOSED, {
        ...open.properties,
        duration_ms: open.elapsed(),
        compared_count: comparisonIds.length,
      })
    }

    function focusCard(id: string) {
      openCard(id, CardOpenSource.CARD)
      setComparisonIds([id])
    }

    function closeOpenCards() {
      ;[...openCardsRef.current.keys()].forEach((id) => closeCard(id))
    }

    function closeComparison() {
      closeOpenCards()
      setComparisonIds([])
    }

    // It fills the screen like a page, so mobile customers swipe back to leave it.
    useCloseOnBack(isOpen, closeComparison)

    useEffect(() => {
      const gone = comparisonIds.filter((id) => !pricedQuotes.some((quote) => quote.id === id))
      if (gone.length === 0) return
      gone.forEach((id) => closeCard(id))
      setComparisonIds((current) => current.filter((id) => !gone.includes(id)))
    })

    const closeOpenCardsRef = useRef(closeOpenCards)
    useEffect(() => {
      closeOpenCardsRef.current = closeOpenCards
    })
    // Unmounting with the viewer open would drop exactly the longest reads from the dwell average.
    useEffect(() => () => closeOpenCardsRef.current(), [])

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }
      // Re-sorting or re-filtering reshuffles the list — drop focus and bring the newly first
      // card (e.g. the cheapest) on screen. Back at the start a leading card shows beside it,
      // unless the screen only fits one card.
      closeComparison()
      const scroller = scrollerRef.current
      if (!scroller) return
      const offset = quotesOffset(scroller)
      const firstQuoteWidth = scroller.querySelector<HTMLElement>('[data-quote-id]')?.offsetWidth ?? 0
      scrollToLeft(offset + firstQuoteWidth <= scroller.clientWidth ? 0 : offset, 'filter_reset')
    }, [orderKey])

    // Rail chip click: add to the comparison, toggle it off (keeping at least one), or — when both
    // slots are full — replace the second slot, keeping the anchor. Never exceeds two.
    function toggleComparison(id: string) {
      const added = !comparisonIds.includes(id)
      // Untoggling the only selected card is a no-op below — don't report a change that didn't happen.
      if (!added && comparisonIds.length === 1) return
      track(QuotesEvent.COMPARISON_TOGGLED, { ...cardProperties(id), added })
      if (!added) {
        closeCard(id)
      } else {
        if (comparisonIds.length >= MAX_COMPARISON) closeCard(comparisonIds[1])
        openCard(id, CardOpenSource.COMPARISON_RAIL)
      }
      setComparisonIds((current) => {
        if (current.includes(id)) {
          return current.length > 1 ? current.filter((existing) => existing !== id) : current
        }
        if (current.length < MAX_COMPARISON) return [...current, id]
        return [current[0], id]
      })
    }

    function removeFromComparison(id: string) {
      track(QuotesEvent.COMPARISON_REMOVED, cardProperties(id))
      closeCard(id)
      setComparisonIds((current) => current.filter((existing) => existing !== id))
    }

    // The scroller covers the scrim, so it dismisses on its own behalf — but only when the
    // click landed on it rather than on a card.
    function dismissOnSelf(event: React.MouseEvent<HTMLElement>) {
      if (event.target === event.currentTarget) closeComparison()
    }

    function isInCart(quote: PricedQuoteCard) {
      const keys = cardCartKeys(quote)
      return keys.length > 0 && keys.every((key) => cartKeys?.has(key))
    }

    function scrollBy(amount: number) {
      scrollerRef.current?.scrollBy({ left: amount, behavior: 'smooth' })
    }

    useImperativeHandle(ref, () => ({
      scrollPrev: () => {
        scrollSourceRef.current = 'arrow'
        scrollBy(-cardStep(scrollerRef.current))
      },
      scrollNext: () => {
        scrollSourceRef.current = 'arrow'
        scrollBy(cardStep(scrollerRef.current))
      },
      scrollToQuote: (id: string) => {
        const scroller = scrollerRef.current
        const card = [...(scroller?.children ?? [])].find(
          (child) => child instanceof HTMLElement && child.dataset.quoteId === id
        )
        if (!scroller || !card) return
        // Measured rather than index × card width: the mobile scroller carries its own left inset.
        const left = card.getBoundingClientRect().left - scroller.getBoundingClientRect().left
        scrollToLeft(scroller.scrollLeft + left, 'new_quote')
      },
      scrollToStart: () => scrollToLeft(0, 'package'),
    }))

    return (
      <>
        {/* Focused viewer: a blur-scrim modal over the populated page. A left rail lists every
          priced quote for quick selection; up to two selected quotes show side by side (desktop).
          Clicking the scrim or pressing Escape returns to the list. */}
        <DialogPrimitive.Root
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeComparison()
          }}
        >
          {isOpen && (
            <DialogPrimitive.Portal>
              {/* z above the sticky page nav (z-100) so the scrim blurs the whole page behind it. */}
              <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm" />
              {/* Scrolls the whole viewport, so the scrollbar sits at the far right of the page. */}
              <DialogPrimitive.Content
                // Marks this as a modal for the agent-panel z-index rule in index.css, like DialogContent.
                data-slot="dialog-content"
                aria-describedby={undefined}
                onClick={dismissOnSelf}
                className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed inset-0 z-[110] overflow-y-auto outline-none"
              >
                {/* Inside the content, not beside it: Radix aria-hides everything outside the
                  dialog and turns pointer events off there, which left this unclickable and
                  invisible to a screen reader. */}
                <DialogPrimitive.Close asChild>
                  <Button
                    type="button"
                    variant="iconOnly"
                    size="icon"
                    aria-label="Close"
                    className="text-ink-700 hover:bg-surface fixed top-4 right-4 z-[120] rounded-full bg-white/90 shadow-md"
                  >
                    <X className="size-5" />
                  </Button>
                </DialogPrimitive.Close>
                <DialogPrimitive.Title className="sr-only">
                  {comparedQuotes.map((quote) => quote.carrierName).join(' vs ')} quote details
                </DialogPrimitive.Title>
                {/* Top-aligned: what a centred box overflows above the scrollport is unreachable. */}
                <div
                  onClick={dismissOnSelf}
                  className={cn('mx-auto flex w-full items-start gap-4 px-4 py-8 md:gap-5', contentWidth)}
                >
                  {/* Panels — one, or two side by side. The second is desktop-only. */}
                  <div className="flex min-w-0 flex-1 items-stretch gap-4">
                    {comparedQuotes.map((quote, index) => (
                      <div
                        key={quote.id}
                        className={cn('flex min-w-0 flex-1 flex-col gap-2', index > 0 && 'max-md:hidden')}
                      >
                        {comparedQuotes.length > 1 && (
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Remove ${quote.carrierName} from comparison`}
                              onClick={() => removeFromComparison(quote.id)}
                              className="text-ink-400 hover:text-ink-700 rounded-full"
                            >
                              <X className="size-4" />
                            </Button>
                          </div>
                        )}
                        <FocusedCardShell reveal={revealFocusedCard}>
                          <QuoteCard
                            quote={quote}
                            position={quotes.indexOf(quote)}
                            isFocused
                            onAddCoverages={onAddCoverages}
                            onRemoveCoverages={onRemoveCoverages}
                            cartKeys={cartKeys}
                            hideCheckout={hideCheckout}
                            onReviewAttestation={onReviewAttestation}
                            onUploadDocument={onUploadDocument}
                          />
                        </FocusedCardShell>
                      </div>
                    ))}
                  </div>
                  {/* Comparison rail — bottom-aligned with the cards, sticking above the viewport floor
                    as they scroll (desktop only). */}
                  {showRail && (
                    <aside className="sticky bottom-8 mt-auto hidden max-h-[calc(100vh-4rem)] w-56 shrink-0 flex-col gap-2 overflow-y-auto md:flex">
                      <span className="text-ink-200 px-1 text-xs font-bold tracking-wide uppercase">
                        Compare with
                      </span>
                      {railQuotes.map((quote) => (
                        <QuoteCardMini
                          key={quote.id}
                          quote={quote}
                          active={comparisonIds.includes(quote.id)}
                          inCart={isInCart(quote)}
                          onClick={() => toggleComparison(quote.id)}
                        />
                      ))}
                    </aside>
                  )}
                </div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </DialogPrimitive.Root>

        <div>
          {/* On mobile, bleed past the page gutter so cards can reach the screen edges
          (the cards are otherwise clipped by the inset). Must not exceed
          that gutter — Container is px-4, and the 5.5 this used to be came from the
          marketing gutter the prototype sits in, which overflowed the page by 6px a
          side and put a horizontal scrollbar on the whole document. */}
          <div className="relative max-md:-mx-4">
            <div
              aria-hidden="true"
              className={cn(
                'from-ink-50 pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r to-transparent transition-opacity duration-200 max-md:hidden',
                canScrollLeft ? 'opacity-100' : 'opacity-0'
              )}
            />
            {/* `overflow-x-auto` forces overflow-y to compute to `auto` (it clips vertically too),
              so the cards need top/bottom padding — otherwise a card's hover lift + shadow is
              cropped at the scroller's edge. */}
            {/* pb-4, not py-2: the cards' hover shadow drops below them and the
                scroller clips at its own edge. no-scrollbar because the arrows above
                are the affordance — the bar under the cards read as page chrome. */}
            <div
              ref={scrollerRef}
              data-cascade-cards
              onScroll={onScroll}
              className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pt-2 pb-4 max-md:scroll-pl-4 max-md:pl-4"
            >
              {leadingCard && (
                <>
                  <div className={cn(QUOTE_CARD_WIDTH, 'flex shrink-0 snap-start')}>{leadingCard}</div>
                  <Separator
                    orientation="vertical"
                    className="via-ink-300 self-stretch bg-transparent bg-gradient-to-b from-transparent to-transparent data-[orientation=vertical]:h-auto"
                  />
                </>
              )}
              {quotes.map((quote, index) => (
                <div
                  key={quote.id}
                  data-carousel-card
                  data-quote-id={quote.id}
                  className={cn(QUOTE_CARD_WIDTH, 'shrink-0 snap-start')}
                >
                  <QuoteCard
                    quote={quote}
                    position={index}
                    onCardClick={focusCard}
                    onDelete={onDeleteQuote}
                    cartKeys={cartKeys}
                    hideCheckout={hideCheckout}
                    onReviewAttestation={onReviewAttestation}
                    onUploadDocument={onUploadDocument}
                  />
                </div>
              ))}
              {/* One announcement for the run — a dozen live regions would each announce. */}
              {awaitingCarrierCount > 0 && (
                <span role="status" className="sr-only">
                  {isLoading
                    ? 'Loading quotes'
                    : `Waiting for ${awaitingCarrierCount} more ${awaitingCarrierCount === 1 ? 'carrier' : 'carriers'}`}
                </span>
              )}
              {Array.from({ length: awaitingCarrierCount }).map((_, index) => (
                <div
                  key={`awaiting-${index}`}
                  data-carousel-card
                  aria-hidden
                  className={cn(QUOTE_CARD_WIDTH, 'shrink-0 snap-start')}
                >
                  <QuoteCardSkeleton />
                </div>
              ))}
              {trailingCard && (
                <div data-carousel-card className={cn(QUOTE_CARD_WIDTH, 'flex shrink-0 snap-start')}>
                  {trailingCard}
                </div>
              )}
            </div>
            <div
              aria-hidden="true"
              className={cn(
                'from-ink-50 pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l to-transparent transition-opacity duration-200 max-md:hidden',
                canScrollRight ? 'opacity-100' : 'opacity-0'
              )}
            />
          </div>
        </div>
      </>
    )
  }
)
